import { Alert } from '@creditornot/cb-alert';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { usePluralization } from 'common/hooks';

import { useCampaignsLimitStatus } from '../hooks/useCampaignsLimitStatus';
import {
  telemetryCampaignsLimitReachedOverlay,
  telemetryCampaignsLimitWarningOverlay,
} from '../utils/telemetry/telemetry';

const CampaignLimitAlertContainer = styled.div`
  padding: 16px;
`;

const CampaignLimitAlert = ({
  campaignsNumberLimit,
  activeCampaignsCount,
}: {
  campaignsNumberLimit: number;
  activeCampaignsCount: number;
}) => {
  const { formatPluralMessage } = usePluralization();
  const intl = useIntl();

  const { hasReachedCampaignsLimit, hasReachedWarningThreshold } = useCampaignsLimitStatus({
    campaignsNumberLimit,
    activeCampaignsCount,
  });

  const [isLimitationAlertDismissed, setIsLimitationAlertDismissed] = useState<boolean>(false);

  useEffect(() => {
    if (hasReachedCampaignsLimit || hasReachedWarningThreshold) {
      setIsLimitationAlertDismissed(false);
    }
  }, [hasReachedCampaignsLimit, hasReachedWarningThreshold]);

  const isAlertVisible = !isLimitationAlertDismissed && (hasReachedCampaignsLimit || hasReachedWarningThreshold);

  useEffect(() => {
    if (isAlertVisible) {
      if (hasReachedCampaignsLimit) {
        telemetryCampaignsLimitReachedOverlay();
      } else {
        telemetryCampaignsLimitWarningOverlay();
      }
    }
  }, [isAlertVisible, hasReachedCampaignsLimit]);

  if (!isAlertVisible) {
    return null;
  }

  return (
    <CampaignLimitAlertContainer>
      {!hasReachedCampaignsLimit ? (
        <Alert
          variant="info"
          size="medium"
          title={formatPluralMessage({
            id: 'campaigns.limitation-messaging.warning-title',
            count: campaignsNumberLimit - activeCampaignsCount,
          })}
          children={intl.formatMessage(
            { id: 'campaigns.limitation-messaging.warning-description' },
            { limitNumber: campaignsNumberLimit },
          )}
          onCloseButtonClick={() => setIsLimitationAlertDismissed(true)}
        />
      ) : (
        <Alert
          variant="warning"
          size="medium"
          title={intl.formatMessage({ id: 'campaigns.limitation-messaging.reached-limit-title' })}
          children={intl.formatMessage(
            { id: 'campaigns.limitation-messaging.reached-limit-description' },
            { limitNumber: campaignsNumberLimit },
          )}
          onCloseButtonClick={() => setIsLimitationAlertDismissed(true)}
        />
      )}
    </CampaignLimitAlertContainer>
  );
};

export default CampaignLimitAlert;
